import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";
import { fetchJson, setAccessTokenGetter, setRefreshCallback } from "../api/http";

type AuthStatus = "loading" | "authenticated" | "unauthenticated";

export interface AuthUser {
  id: string;
  email: string;
  username?: string | null;
  tenant_roles?: Record<string, string>;
}

export interface AuthContextValue {
  status: AuthStatus;
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;

  loginWithPassword: (input: { email: string; password: string }) => Promise<void>;
  setTokens: (input: { accessToken: string; refreshToken: string }) => Promise<void>;
  refresh: () => Promise<string | null>;
  logout: () => void;
  /** Sync user from hierarchy response. */
  syncUserFromHierarchy: (user: AuthUser | null) => void;
}

const STORAGE_KEY = "openmon.auth";
const REFRESH_SKEW_MS = 60_000; // refresh 60s before expiry
const MAX_AUTO_REFRESH_DELAY_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

type StoredAuth = {
  accessToken: string | null;
  refreshToken: string | null;
};

function safeParseJson(value: string): unknown {
  try {
    return JSON.parse(value) as unknown;
  } catch {
    return null;
  }
}

function decodeBase64UrlToString(value: string): string | null {
  try {
    const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "===".slice((base64.length + 3) % 4);
    return atob(padded);
  } catch {
    return null;
  }
}

function getJwtExpMs(token: string): number | null {
  const parts = token.split(".");
  if (parts.length < 2) return null;
  const decoded = decodeBase64UrlToString(parts[1] ?? "");
  if (!decoded) return null;
  const payload = safeParseJson(decoded);
  if (!payload || typeof payload !== "object") return null;
  const exp = (payload as { exp?: unknown }).exp;
  return typeof exp === "number" && Number.isFinite(exp) ? exp * 1000 : null;
}

function readStoredAuth(): StoredAuth {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return { accessToken: null, refreshToken: null };
  const parsed = safeParseJson(raw);
  if (!parsed || typeof parsed !== "object") return { accessToken: null, refreshToken: null };
  const obj = parsed as Partial<StoredAuth>;
  return {
    accessToken: typeof obj.accessToken === "string" ? obj.accessToken : null,
    refreshToken: typeof obj.refreshToken === "string" ? obj.refreshToken : null,
  };
}

function writeStoredAuth(value: StoredAuth): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
}

async function loginWithPasswordApi(
  input: { email: string; password: string },
  signal?: AbortSignal,
): Promise<{ access_token: string; refresh_token: string; user?: AuthUser | null }> {
  return fetchJson<{ access_token: string; refresh_token: string; user?: AuthUser | null }>(
    "/auth/login",
    {
      method: "POST",
      body: { email: input.email, password: input.password },
      signal,
      skipAuth: true, // Public endpoint
    },
    undefined,
  );
}

async function refreshApi(
  input: { refresh_token: string },
  signal?: AbortSignal,
): Promise<{ access_token: string; refresh_token?: string }> {
  return fetchJson<{ access_token: string; refresh_token?: string }>(
    "/auth/refresh",
    {
      method: "POST",
      body: input,
      signal,
      skipAuth: true, // Public endpoint (uses refresh token in body)
    },
    undefined,
  );
}

async function logoutApi(input: { refresh_token: string }, signal?: AbortSignal): Promise<void> {
  await fetchJson<unknown>(
    "/auth/logout",
    {
      method: "POST",
      body: input,
      signal,
      skipAuth: true, // Public endpoint (uses refresh token in body)
    },
    undefined,
  );
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);

  const refreshInFlight = useRef<Promise<string | null> | null>(null);
  const refreshTimerId = useRef<number | null>(null);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerId.current !== null) {
      window.clearTimeout(refreshTimerId.current);
      refreshTimerId.current = null;
    }
  }, []);

  const persist = useCallback((next: { access: string | null; refresh: string | null }) => {
    writeStoredAuth({ accessToken: next.access, refreshToken: next.refresh });
  }, []);

  const applyTokens = useCallback(
    async (next: { access: string | null; refresh: string | null; nextUser?: AuthUser | null }) => {
      setAccessToken(next.access);
      setRefreshToken(next.refresh);
      persist({ access: next.access, refresh: next.refresh });

      if (next.access && next.refresh) {
        setStatus("authenticated");
        if (next.nextUser !== undefined) {
          setUser(next.nextUser);
        }
      } else {
        setUser(null);
        setStatus("unauthenticated");
      }
    },
    [persist],
  );

  const logout = useCallback(() => {
    const token = refreshToken;
    clearRefreshTimer();
    refreshInFlight.current = null;
    void (async () => {
      if (token) {
        try {
          await logoutApi({ refresh_token: token });
        } catch {
          // ignore network errors; local logout should still proceed
        }
      }
    })();
    void applyTokens({ access: null, refresh: null, nextUser: null });
  }, [applyTokens, clearRefreshTimer, refreshToken]);

  const refresh = useCallback(async (): Promise<string | null> => {
    if (!refreshToken) return null;

    if (refreshInFlight.current) return refreshInFlight.current;

    const p = (async () => {
      try {
        const res = await refreshApi({ refresh_token: refreshToken });
        const nextAccess = res.access_token;
        const nextRefresh = res.refresh_token ?? refreshToken;
        await applyTokens({ access: nextAccess, refresh: nextRefresh });
        return nextAccess;
      } catch {
        // Refresh failed => treat as logged out
        logout();
        return null;
      } finally {
        refreshInFlight.current = null;
      }
    })();

    refreshInFlight.current = p;
    return p;
  }, [applyTokens, logout, refreshToken]);

  const setTokens = useCallback(
    async (input: { accessToken: string; refreshToken: string }): Promise<void> => {
      await applyTokens({ access: input.accessToken, refresh: input.refreshToken });
    },
    [applyTokens],
  );

  const loginWithPassword = useCallback(
    async (input: { email: string; password: string }): Promise<void> => {
      const res = await loginWithPasswordApi(input);
      await applyTokens({
        access: res.access_token,
        refresh: res.refresh_token,
        nextUser: res.user ?? null,
      });
    },
    [applyTokens],
  );

  const syncUserFromHierarchy = useCallback((hierarchyUser: AuthUser | null) => {
    setUser((prev) => {
      if (hierarchyUser == null) return null;
      if (prev && prev.id === hierarchyUser.id) {
        return { ...prev, ...hierarchyUser };
      }
      return hierarchyUser;
    });
  }, []);

  // Register access token getter and refresh callback for API layer (401 retry)
  useEffect(() => {
    setAccessTokenGetter(() => accessToken);
    setRefreshCallback(() => refresh());
    return () => {
      setAccessTokenGetter(() => null);
      setRefreshCallback(() => Promise.resolve(null));
    };
  }, [accessToken, refresh]);

  // Initial load from storage. User is populated by HierarchyContext via syncUserFromHierarchy when hierarchy loads.
  useEffect(() => {
    const stored = readStoredAuth();
    setAccessToken(stored.accessToken);
    setRefreshToken(stored.refreshToken);
    setStatus(stored.accessToken && stored.refreshToken ? "authenticated" : "unauthenticated");
  }, []);

  // Cross-tab sync
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key !== STORAGE_KEY) return;
      const stored = readStoredAuth();
      setAccessToken(stored.accessToken);
      setRefreshToken(stored.refreshToken);
      setStatus(stored.accessToken && stored.refreshToken ? "authenticated" : "unauthenticated");
      if (!stored.accessToken || !stored.refreshToken) {
        setUser(null);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Auto-refresh scheduling based on access token exp claim
  useEffect(() => {
    clearRefreshTimer();
    if (!accessToken || !refreshToken) return;

    const expMs = getJwtExpMs(accessToken);
    if (!expMs) return;

    const now = Date.now();
    const delay = Math.min(Math.max(expMs - now - REFRESH_SKEW_MS, 0), MAX_AUTO_REFRESH_DELAY_MS);
    refreshTimerId.current = window.setTimeout(() => {
      void refresh();
    }, delay);

    return () => clearRefreshTimer();
  }, [accessToken, clearRefreshTimer, refresh, refreshToken]);

  // If the token is already expired on mount/wake, attempt refresh once.
  useEffect(() => {
    if (!accessToken || !refreshToken) return;
    const expMs = getJwtExpMs(accessToken);
    if (!expMs) return;
    if (Date.now() >= expMs) {
      void refresh();
    }
  }, [accessToken, refresh, refreshToken]);

  // When user returns to the tab, refresh if token is close to expiry or already expired
  useEffect(() => {
    function onVisibilityChange() {
      if (document.visibilityState !== "visible") return;
      if (!accessToken || !refreshToken) return;
      const expMs = getJwtExpMs(accessToken);
      if (!expMs) return;
      const now = Date.now();
      if (now >= expMs || now >= expMs - REFRESH_SKEW_MS) {
        void refresh();
      }
    }
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [accessToken, refresh, refreshToken]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      accessToken,
      refreshToken,
      user,
      loginWithPassword,
      setTokens,
      refresh,
      logout,
      syncUserFromHierarchy,
    }),
    [accessToken, loginWithPassword, logout, refresh, refreshToken, setTokens, status, syncUserFromHierarchy, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}

